import { useCallback } from 'react'
import { X, Printer, CheckCircle, ArrowRight, Phone, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from './DiscoveryWizard'
import { formatPrice } from '@/lib/pricing-data'
import { formatCurrency, formatPercent, formatPayback } from '@/lib/value-calculator'
import {
  useCases,
  gpsDenialOptions,
  mitigationOptions,
  valueMessages,
} from '@/lib/discovery-data'

interface ProposalPDFProps {
  onClose: () => void
}

export function ProposalPDF({ onClose }: ProposalPDFProps) {
  const { state, valueMetrics, quoteItems, totalInvestment } = useDiscoveryContext()

  const useCase = useCases.find((u) => u.id === state.useCase)
  const gpsDenial = gpsDenialOptions.find((o) => o.value === state.challenges.gpsDenialFrequency)
  const mitigation = mitigationOptions.find((o) => o.value === state.challenges.currentMitigation)
  const messages = state.useCase ? valueMessages[state.useCase] : []

  const quoteDate = new Date()
  const validUntil = new Date()
  validUntil.setDate(validUntil.getDate() + 30)

  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  if (!valueMetrics || !useCase) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-auto py-8',
        'modal-backdrop-enter'
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-white text-gray-900 rounded-lg shadow-2xl w-full max-w-4xl mx-4',
          'modal-content-enter'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header (not printed) */}
        <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200 print:hidden">
          <h2 className="text-lg font-medium text-gray-900">Proposal Preview</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Printable Content */}
        <div className="p-8 print:p-12 space-y-8">
          {/* ========== HEADER ========== */}
          <div className="flex items-start justify-between pb-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center">
                <span className="text-[#63d706] font-bold text-2xl">T</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Theseus</h1>
                <p className="text-gray-500">Visual Positioning System</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">PROPOSAL</p>
              <p className="text-sm text-gray-500 mt-1">
                {quoteDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* ========== PREPARED FOR ========== */}
          <div className="bg-gray-50 rounded-lg p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Prepared For</p>
            <p className="text-2xl font-semibold text-gray-900">{state.customerName}</p>
            {state.customerCompany && (
              <p className="text-gray-600">{state.customerCompany}</p>
            )}
            <p className="text-sm text-gray-500 mt-3">
              Valid until:{' '}
              {validUntil.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          {/* ========== EXECUTIVE SUMMARY ========== */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                1
              </span>
              Executive Summary
            </h2>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                Based on your {useCase.name.toLowerCase()} operations with{' '}
                <strong>{state.missionProfile.fleetSize} platforms</strong> and{' '}
                <strong>{state.missionProfile.monthlyFlightHours} monthly flight hours</strong>,
                Cyclops can deliver{' '}
                <strong className="text-green-700">
                  {formatCurrency(valueMetrics.projectedSavings)} in annual cost savings
                </strong>{' '}
                by recovering missions impacted by GPS denial. With a projected{' '}
                <strong>{valueMetrics.threeYearROI}% ROI over three years</strong>, this investment
                pays for itself in{' '}
                <strong>{formatPayback(valueMetrics.paybackPeriodMonths)}</strong>.
              </p>
            </div>
          </section>

          {/* ========== SITUATION ANALYSIS ========== */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                2
              </span>
              Situation Analysis
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Use Case</p>
                <p className="font-medium text-gray-900">{useCase.name}</p>
                <p className="text-sm text-gray-500 mt-1">{useCase.tagline}</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Fleet Size</p>
                <p className="font-medium text-gray-900">
                  {state.missionProfile.fleetSize} platforms
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {state.missionProfile.monthlyFlightHours} hours/month
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">GPS Denial Frequency</p>
                <p className="font-medium text-gray-900">{gpsDenial?.label}</p>
                <p className="text-sm text-gray-500 mt-1">{gpsDenial?.description}</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Current Approach</p>
                <p className="font-medium text-gray-900">{mitigation?.label}</p>
                <p className="text-sm text-gray-500 mt-1">{mitigation?.description}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-2xl font-bold text-red-600">
                  {state.challenges.missionAbortRate}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Mission Abort Rate</p>
              </div>
              <div className="text-center p-4 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(state.challenges.costPerFailedMission)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Cost per Failure</p>
              </div>
              <div className="text-center p-4 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(valueMetrics.annualFailureCost)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Annual Impact</p>
              </div>
            </div>
          </section>

          {/* ========== SOLUTION FIT ========== */}
          <section className="page-break-before">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                3
              </span>
              Solution Fit
            </h2>
            <p className="text-gray-600 mb-4">
              Cyclops provides GPS-denied navigation for your {useCase.name.toLowerCase()}{' '}
              operations, delivering:
            </p>
            <div className="grid grid-cols-1 gap-3">
              {messages.map((msg) => (
                <div key={msg.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{msg.title}</p>
                    <p className="text-sm text-gray-500">{msg.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ========== VALUE PROJECTION ========== */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                4
              </span>
              Value Projection
            </h2>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <ValueMetricCard
                label="Annual Savings"
                value={formatCurrency(valueMetrics.projectedSavings)}
                accent
              />
              <ValueMetricCard
                label="Success Rate"
                value={formatPercent(valueMetrics.projectedSuccessRate)}
                subtitle={`+${valueMetrics.successRateImprovement.toFixed(1)}%`}
              />
              <ValueMetricCard
                label="3-Year ROI"
                value={`${valueMetrics.threeYearROI}%`}
              />
              <ValueMetricCard
                label="Payback"
                value={formatPayback(valueMetrics.paybackPeriodMonths)}
              />
            </div>
            <div className="flex items-center justify-center gap-8 py-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-500">
                  {formatPercent(valueMetrics.currentSuccessRate)}
                </p>
                <p className="text-sm text-gray-500">Current Success Rate</p>
              </div>
              <ArrowRight className="w-8 h-8 text-green-600" />
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {formatPercent(valueMetrics.projectedSuccessRate)}
                </p>
                <p className="text-sm text-gray-500">With Cyclops</p>
              </div>
            </div>
          </section>

          {/* ========== INVESTMENT BREAKDOWN ========== */}
          <section className="page-break-before">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                5
              </span>
              Investment Breakdown
            </h2>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 text-xs font-semibold text-gray-500 uppercase">
                    Item
                  </th>
                  <th className="text-left py-3 text-xs font-semibold text-gray-500 uppercase">
                    Part Number
                  </th>
                  <th className="text-right py-3 text-xs font-semibold text-gray-500 uppercase">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {quoteItems.map((qi) => (
                  <tr key={qi.item.id} className="border-b border-gray-100">
                    <td className="py-3">
                      <p className="font-medium text-gray-900">{qi.item.name}</p>
                      <p className="text-sm text-gray-500">{qi.item.description}</p>
                    </td>
                    <td className="py-3 text-sm text-gray-500 font-mono">
                      {qi.item.partNumber}
                    </td>
                    <td className="py-3 text-right font-medium text-gray-900">
                      {formatPrice(qi.item.price * qi.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={2} className="py-4 text-right font-semibold text-gray-900">
                    Total Investment
                  </td>
                  <td className="py-4 text-right text-2xl font-bold text-gray-900">
                    {formatCurrency(totalInvestment)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* ========== NEXT STEPS ========== */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs">
                6
              </span>
              Next Steps
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg text-center">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-gray-900">1</span>
                </div>
                <p className="font-medium text-gray-900">Technical Review</p>
                <p className="text-sm text-gray-500">Integration assessment call</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg text-center">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-gray-900">2</span>
                </div>
                <p className="font-medium text-gray-900">Pilot Program</p>
                <p className="text-sm text-gray-500">Validate in your environment</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg text-center">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-gray-900">3</span>
                </div>
                <p className="font-medium text-gray-900">Deployment</p>
                <p className="text-sm text-gray-500">Full fleet rollout</p>
              </div>
            </div>
          </section>

          {/* ========== FOOTER ========== */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">Theseus Inc.</p>
                <p className="text-sm text-gray-500">theseus.us</p>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  sales@theseus.us
                </span>
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Sales
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .fixed.inset-0,
          .fixed.inset-0 * {
            visibility: visible;
          }
          .fixed.inset-0 {
            position: absolute;
            inset: 0;
            background: white !important;
            overflow: visible;
          }
          .fixed.inset-0 > div {
            box-shadow: none !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            border-radius: 0 !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .page-break-before {
            page-break-before: always;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
        }
      `}</style>
    </div>
  )
}

interface ValueMetricCardProps {
  label: string
  value: string
  subtitle?: string
  accent?: boolean
}

function ValueMetricCard({ label, value, subtitle, accent }: ValueMetricCardProps) {
  return (
    <div
      className={cn(
        'p-4 rounded-lg text-center',
        accent ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
      )}
    >
      <p className="text-xs text-gray-500 uppercase mb-1">{label}</p>
      <p className={cn('text-2xl font-bold', accent ? 'text-green-600' : 'text-gray-900')}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-green-600 mt-1">{subtitle}</p>}
    </div>
  )
}

